export interface PlayCenterMenu {
  id: string;
  name: { sv: string; en: string };
  pricePerChild: number | null;
  includes: { sv: string; en: string };
}

export interface PlayCenterRoom {
  id: string;
  name: string;
}

export interface PlayCenter {
  id: string;
  name: string;
  address: string;
  description: { sv: string; en: string };
  seoText: { sv: string; en: string };
  website?: string;
  menus: PlayCenterMenu[];
  partyRooms: PlayCenterRoom[];
}

const leosMenus: PlayCenterMenu[] = [
  {
    id: "leos-dromkalaset",
    name: { sv: "Drömkalaset", en: "Dream Party" },
    pricePerChild: null,
    includes: {
      sv: "Kalasrum 60 min, entré, godispåse, djungelsaft, popcorn, kalaskaffe till 2 vuxna",
      en: "Party room 60 min, entry, candy bag, jungle juice, popcorn, party coffee for 2 adults",
    },
  },
  {
    id: "leos-dromkalaset-glassbomb",
    name: { sv: "Drömkalaset med glassbomb", en: "Dream Party with Ice Cream Sundae" },
    pricePerChild: null,
    includes: {
      sv: "Kalasrum 60 min, entré, godispåse, djungelsaft, popcorn, kalaskaffe till 2 vuxna + kalasvård, glassbomb, present till kalasbarnet",
      en: "Party room 60 min, entry, candy bag, jungle juice, popcorn, coffee for 2 adults + party care, ice cream sundae, gift for birthday child",
    },
  },
  {
    id: "leos-dromkalaset-glassbomb-varmkorv",
    name: { sv: "Drömkalaset med glassbomb och varmkorv", en: "Dream Party with Ice Cream & Hot Dog" },
    pricePerChild: null,
    includes: {
      sv: "Drömkalaset med glassbomb + varmkorv till barnen",
      en: "Dream Party with ice cream sundae + hot dog for the kids",
    },
  },
  {
    id: "leos-dromkalaset-glassbomb-pannkakor",
    name: { sv: "Drömkalaset med glassbomb och pannkakor", en: "Dream Party with Ice Cream & Pancakes" },
    pricePerChild: null,
    includes: {
      sv: "Drömkalaset med glassbomb + pannkakor till alla barn",
      en: "Dream Party with ice cream sundae + pancakes for all kids",
    },
  },
  {
    id: "leos-dromkalaset-glassbomb-pizza",
    name: { sv: "Drömkalaset med glassbomb och pizza", en: "Dream Party with Ice Cream & Pizza" },
    pricePerChild: null,
    includes: {
      sv: "Drömkalaset med glassbomb + pizzaslice (Margherita)",
      en: "Dream Party with ice cream sundae + pizza slice (Margherita)",
    },
  },
  {
    id: "leos-dromkalaset-glassbomb-hamburgare",
    name: { sv: "Drömkalaset med glassbomb, hamburgare och pommes", en: "Dream Party with Ice Cream, Burger & Fries" },
    pricePerChild: null,
    includes: {
      sv: "Drömkalaset med glassbomb + hamburgare med pommes",
      en: "Dream Party with ice cream sundae + burger with fries",
    },
  },
  {
    id: "leos-dromkalaset-cool-slush",
    name: { sv: "Drömkalaset med cool slush", en: "Dream Party with Cool Slush" },
    pricePerChild: null,
    includes: {
      sv: "Drömkalaset + kalasvård, glassbomb, present, slushmuggar med slush",
      en: "Dream Party + party care, ice cream sundae, gift, slush cups with slush",
    },
  },
];

const leosDescription = {
  sv: "Leo's Lekland – stort lekland med klätterställningar, rutschkanor, trampoliner och kalaspaket.",
  en: "Leo's Playland – large play center with climbing frames, slides, trampolines, and party packages.",
};

export const playCenters: PlayCenter[] = [
  {
    id: "stellas-uddevalla",
    name: "Stellas Lekland, Uddevalla",
    address: "Junogatan 4, 451 49 Uddevalla",
    description: {
      sv: "Lekland med klätterställning, bollhav, hopphus och kalas-paket. Eget kalasrum ingår.",
      en: "Play center with climbing frames, ball pit, bouncy house, and party packages. Private party room included.",
    },
    seoText: {
      sv: "Planerar du barnkalas på Stellas Lekland i Uddevalla? Stellas erbjuder en fantastisk lekmiljö med klätterställningar, bollhav och hopphus – perfekt för barn i alla åldrar. Välj mellan flera kalaspaket med mat, godispåse och present till kalasbarnet. Varje kalas inkluderar ett eget kalasrum i valfritt tema: Prinsessrummet, Discorummet, Superhjälterummet eller Undervattensrummet. Använd Svara Nu för att skicka inbjudan, samla OSA och låta gästerna rösta på datum – helt gratis!",
      en: "Planning a kids party at Stellas Lekland in Uddevalla? Stellas offers a fantastic play environment with climbing frames, ball pit, and bouncy house – perfect for children of all ages. Choose from several party packages with food, candy bag, and a gift for the birthday child. Each party includes a private themed room: Princess Room, Disco Room, Superhero Room, or Underwater Room. Use Svara Nu to send invitations, collect RSVPs, and let guests vote on dates – completely free!",
    },
    website: "https://www.stellaslekland.se",
    menus: [
      {
        id: "stellas-kladdkaka",
        name: { sv: "Kladdkakskalas", en: "Chocolate Cake Party" },
        pricePerChild: 240,
        includes: {
          sv: "Kladdkaka med grädde, festis, godispåse, present, kaffe till föräldrar",
          en: "Chocolate cake with cream, juice, candy bag, gift, coffee for parents",
        },
      },
      {
        id: "stellas-pannkaka",
        name: { sv: "Pannkakskalas", en: "Pancake Party" },
        pricePerChild: 240,
        includes: {
          sv: "Pannkakor med grädde och sylt, festis, godispåse, present, kaffe till föräldrar",
          en: "Pancakes with cream and jam, juice, candy bag, gift, coffee for parents",
        },
      },
      {
        id: "stellas-hamburgare",
        name: { sv: "Hamburgerkalas", en: "Burger Party" },
        pricePerChild: 260,
        includes: {
          sv: "Hamburgare med bröd, pommes, festis, godispåse, present, kaffe till föräldrar",
          en: "Burger with bun, fries, juice, candy bag, gift, coffee for parents",
        },
      },
      {
        id: "stellas-korv",
        name: { sv: "Korv & pommes-kalas", en: "Hot Dog & Fries Party" },
        pricePerChild: 260,
        includes: {
          sv: "Kokt korv, pommes, festis, godispåse, present, kaffe till föräldrar",
          en: "Boiled hot dog, fries, juice, candy bag, gift, coffee for parents",
        },
      },
    ],
    partyRooms: [
      { id: "stellas-prinsess", name: "Prinsessrummet" },
      { id: "stellas-disco", name: "Discorummet" },
      { id: "stellas-superhjalte", name: "Superhjälterummet" },
      { id: "stellas-undervatten", name: "Undervattensrummet" },
    ],
  },
  {
    id: "funplace-trestad",
    name: "FunPlace, Trestad Center",
    address: "Överbyvägen 2, 461 39 Trollhättan",
    description: {
      sv: "Stort lekland i Trestad Center med trampoliner, hinderbana och kalas-upplägg med mat.",
      en: "Large play center in Trestad Center with trampolines, obstacle course, and party packages with food.",
    },
    seoText: {
      sv: "Fira barnkalas på FunPlace i Trestad Center, Trollhättan! Med trampoliner, hinderbana och massor av lek väntar en oförglömlig upplevelse. Välj bland kalaspaketen Diamant, Guld, Silver eller VIP – alla med kalasrum, värdinna och roliga aktiviteter. FunPlace Fairy Princess-rummet gör festen extra magisk. Boka enkelt med Svara Nu – skicka digitala inbjudningar och låt gästerna rösta på bästa datum.",
      en: "Celebrate a kids party at FunPlace in Trestad Center, Trollhättan! With trampolines, obstacle courses, and tons of play, an unforgettable experience awaits. Choose from Diamond, Gold, Silver, or VIP party packages – all with party room, host, and fun activities. The FunPlace Fairy Princess room makes the celebration extra magical. Book easily with Svara Nu – send digital invitations and let guests vote on the best date.",
    },
    website: "https://www.funplace.se",
    menus: [
      {
        id: "funplace-diamant",
        name: { sv: "Diamant", en: "Diamond" },
        pricePerChild: 279,
        includes: {
          sv: "Allt inkluderat: entré, kalasrum 55 min, kalasvärdinna, vald meny, glassbomb, godispåse, present",
          en: "All inclusive: entry, party room 55 min, party host, chosen menu, ice cream sundae, candy bag, gift",
        },
      },
      {
        id: "funplace-guld",
        name: { sv: "Guld", en: "Gold" },
        pricePerChild: null,
        includes: {
          sv: "Massor av skoj och extra lyx",
          en: "Lots of fun and extra luxury",
        },
      },
      {
        id: "funplace-silver",
        name: { sv: "Silver", en: "Silver" },
        pricePerChild: null,
        includes: {
          sv: "Klassiskt och festligt födelsedagspaket",
          en: "Classic and festive birthday package",
        },
      },
      {
        id: "funplace-vip",
        name: { sv: "VIP", en: "VIP" },
        pricePerChild: null,
        includes: {
          sv: "För den lilla festen med stora drömmar",
          en: "For the small party with big dreams",
        },
      },
    ],
    partyRooms: [
      { id: "funplace-fairy", name: "Fairy Princess" },
    ],
  },
  {
    id: "leos-gbg-backaplan",
    name: "Leo's Lekland, Göteborg Backaplan",
    address: "Herkulesgatan 1, 417 03 Göteborg",
    description: leosDescription,
    seoText: {
      sv: "Planerar du kalas på Leo's Lekland Backaplan i Göteborg? Leo's erbjuder ett av stadens största lekland med klätterställningar, rutschkanor och trampoliner. Välj bland flera Drömkalas-paket med allt från glassbomb till hamburgare och pizza. Alla paket inkluderar kalasrum i 60 minuter, entré, godispåse och kalaskaffe. Skapa inbjudan med Svara Nu, dela via SMS eller WhatsApp och låt gästerna rösta på datum som passar.",
      en: "Planning a party at Leo's Lekland Backaplan in Gothenburg? Leo's offers one of the city's largest play centers with climbing frames, slides, and trampolines. Choose from several Dream Party packages including ice cream sundae, burger, or pizza options. All packages include 60 min party room, entry, candy bag, and coffee. Create invitations with Svara Nu, share via SMS or WhatsApp, and let guests vote on dates.",
    },
    website: "https://www.leoslekland.se",
    menus: leosMenus,
    partyRooms: [],
  },
  {
    id: "leos-gbg-molndal",
    name: "Leo's Lekland, Göteborg Mölndal",
    address: "Arnegårdsgatan 12, 431 53 Mölndal",
    description: leosDescription,
    seoText: {
      sv: "Fira kalas på Leo's Lekland Mölndal! Nära Göteborg centrum hittar du ett stort lekland med klätterställningar, rutschkanor och trampoliner som barnen älskar. Välj ett av de populära Drömkalas-paketen – med eller utan mat, glassbomb och present. Kalasrum i 60 minuter ingår. Med Svara Nu skapar du en digital inbjudan på några minuter, delar den enkelt och får svar direkt.",
      en: "Celebrate at Leo's Lekland Mölndal! Close to central Gothenburg, you'll find a large play center with climbing frames, slides, and trampolines that kids love. Choose one of the popular Dream Party packages – with or without food, ice cream sundae, and gift. Party room for 60 minutes included. With Svara Nu, create a digital invitation in minutes, share easily, and get responses instantly.",
    },
    website: "https://www.leoslekland.se",
    menus: leosMenus,
    partyRooms: [],
  },
  {
    id: "boda-borg-goteborg",
    name: "Boda Borg, Göteborg",
    address: "Importgatan 4, 422 46 Hisings Backa",
    description: {
      sv: "Boda Borg – upplevelsebaserat äventyrshus med quests, fysiska utmaningar och samarbetsövningar för barn och vuxna.",
      en: "Boda Borg – experience-based adventure house with quests, physical challenges, and teamwork exercises for kids and adults.",
    },
    seoText: {
      sv: "Planerar du barnkalas på Boda Borg i Göteborg? Boda Borg erbjuder ett helt unikt kalaskoncept där barnen löser quests tillsammans – en blandning av fysiska utmaningar, problemlösning och samarbete som passar perfekt för barn från 7 år och uppåt. Varje quest är ett eget rum med olika teman och svårighetsgrader. Kalasen bokas med egen festlokal där barnen kan äta medhavd eller beställd mat mellan äventyren. Använd Svara Nu för att skicka digitala inbjudningar, samla OSA-svar och låta gästerna rösta på det bästa datumet – helt gratis!",
      en: "Planning a kids party at Boda Borg in Gothenburg? Boda Borg offers a completely unique party concept where children solve quests together – a mix of physical challenges, problem-solving, and teamwork perfect for kids aged 7 and up. Each quest is a separate room with different themes and difficulty levels. Parties are booked with a private party area where kids can eat between adventures. Use Svara Nu to send digital invitations, collect RSVPs, and let guests vote on the best date – completely free!",
    },
    website: "https://www.bodaborg.se",
    menus: [
      {
        id: "boda-borg-basic",
        name: { sv: "Kalaspaket Bas", en: "Basic Party Package" },
        pricePerChild: 295,
        includes: {
          sv: "2 timmars spel, eget kalasrum 45 min, festdukning, saft och popcorn",
          en: "2 hours of quests, private party room 45 min, party setup, juice and popcorn",
        },
      },
      {
        id: "boda-borg-plus",
        name: { sv: "Kalaspaket Plus", en: "Plus Party Package" },
        pricePerChild: 345,
        includes: {
          sv: "2 timmars spel, eget kalasrum 45 min, pizza, saft, glass och godispåse",
          en: "2 hours of quests, private party room 45 min, pizza, juice, ice cream, and candy bag",
        },
      },
    ],
    partyRooms: [
      { id: "boda-borg-party-1", name: "Questrummet" },
    ],
  },
  {
    id: "jump-yard-goteborg",
    name: "Jump Yard, Göteborg",
    address: "Grafiska vägen 6, 412 63 Göteborg",
    description: {
      sv: "Jump Yard – trampolinpark med frizoner, dodgeball, skumgropar och kalaspaket med egen fest-yta.",
      en: "Jump Yard – trampoline park with free zones, dodgeball, foam pits, and party packages with private party area.",
    },
    seoText: {
      sv: "Fira barnkalas på Jump Yard i Göteborg! Med hundratals sammankopplade trampoliner, dodgeball-planer, skumgropar och ninja warrior-banor väntar en energifylld upplevelse som barnen aldrig glömmer. Jump Yard erbjuder flera kalaspaket där hoppning, eget kalasrum och mat ingår. Perfekt för barn från 5 år som vill röra på sig och ha kul med kompisarna. Skapa en digital inbjudan med Svara Nu – skicka via SMS eller WhatsApp, samla svar och låt gästerna rösta på datum som passar bäst.",
      en: "Celebrate a kids party at Jump Yard in Gothenburg! With hundreds of connected trampolines, dodgeball courts, foam pits, and ninja warrior courses, an energy-filled experience awaits that kids will never forget. Jump Yard offers several party packages including jumping time, private party room, and food. Perfect for children aged 5+ who want to move and have fun with friends. Create a digital invitation with Svara Nu – send via SMS or WhatsApp, collect responses, and let guests vote on dates.",
    },
    website: "https://www.jumpyard.se",
    menus: [
      {
        id: "jumpyard-kalas-bas",
        name: { sv: "Hoppkalas Bas", en: "Jump Party Basic" },
        pricePerChild: 259,
        includes: {
          sv: "60 min hoppning, hoppstrumpor, kalasrum 45 min, saft och chips",
          en: "60 min jumping, jump socks, party room 45 min, juice and chips",
        },
      },
      {
        id: "jumpyard-kalas-plus",
        name: { sv: "Hoppkalas Plus", en: "Jump Party Plus" },
        pricePerChild: 319,
        includes: {
          sv: "60 min hoppning, hoppstrumpor, kalasrum 45 min, pizza, saft, glass och godispåse",
          en: "60 min jumping, jump socks, party room 45 min, pizza, juice, ice cream, and candy bag",
        },
      },
      {
        id: "jumpyard-kalas-premium",
        name: { sv: "Hoppkalas Premium", en: "Jump Party Premium" },
        pricePerChild: 379,
        includes: {
          sv: "90 min hoppning, hoppstrumpor, kalasrum 60 min, hamburgare, pommes, saft, tårta och godispåse",
          en: "90 min jumping, jump socks, party room 60 min, burger, fries, juice, cake, and candy bag",
        },
      },
    ],
    partyRooms: [
      { id: "jumpyard-room-1", name: "Studsmattan" },
      { id: "jumpyard-room-2", name: "Fliprummet" },
    ],
  },
  {
    id: "leos-boras",
    name: "Leo's Lekland, Borås",
    address: "Skaraborgsvägen 3A, 506 30 Borås",
    description: leosDescription,
    seoText: {
      sv: "Planerar du barnkalas på Leo's Lekland i Borås? Leo's i Borås erbjuder en av Sjuhärads största lekmiljöer med klätterställningar, rutschkanor och trampoliner. Alla Drömkalas-paket inkluderar kalasrum i 60 minuter, entré för alla barn, godispåse och kalaskaffe till två vuxna. Uppgradera med glassbomb, pizza eller hamburgare. Boka enkelt med Svara Nu – skapa en digital inbjudan, dela den direkt och samla svar från gästerna.",
      en: "Planning a kids party at Leo's Lekland in Borås? Leo's in Borås offers one of the largest play environments in the Sjuhärad region with climbing frames, slides, and trampolines. All Dream Party packages include 60 min party room, entry for all children, candy bag, and coffee for two adults. Upgrade with ice cream sundae, pizza, or burger. Book easily with Svara Nu – create a digital invitation, share instantly, and collect guest responses.",
    },
    website: "https://www.leoslekland.se",
    menus: leosMenus,
    partyRooms: [],
  },
  {
    id: "leos-trollhattan",
    name: "Leo's Lekland, Trollhättan",
    address: "Verkmästarevägen 2, 461 38 Trollhättan",
    description: leosDescription,
    seoText: {
      sv: "Fira barnkalas på Leo's Lekland i Trollhättan! Med ett stort lekland fullt av klätterställningar, trampoliner och rutschkanor har barnen garanterat kul i timmar. Leo's Drömkalas-paket innehåller allt från godispåsar och kalaskaffe till glassbomber och pizza. Kalasrum i 60 minuter ingår alltid. Skapa inbjudan med Svara Nu – dela via SMS, samla OSA och låt gästerna rösta på det datum som passar flest.",
      en: "Celebrate a kids party at Leo's Lekland in Trollhättan! With a large play center full of climbing frames, trampolines, and slides, kids are guaranteed hours of fun. Leo's Dream Party packages include everything from candy bags and party coffee to ice cream sundaes and pizza. 60 min party room always included. Create invitations with Svara Nu – share via SMS, collect RSVPs, and let guests vote on the best date.",
    },
    website: "https://www.leoslekland.se",
    menus: leosMenus,
    partyRooms: [],
  },
  {
    id: "galaxen-ullared",
    name: "Galaxen Äventyrsland, Ullared",
    address: "Lassalyckans industriområde, 311 44 Falkenberg",
    description: {
      sv: "Galaxen Äventyrsland – stort lekland nära Gekås med rymdtema, klätternät, rutschkanor och kalaspaket.",
      en: "Galaxen Adventure Land – large play center near Gekås with space theme, climbing nets, slides, and party packages.",
    },
    seoText: {
      sv: "Planerar du barnkalas på Galaxen Äventyrsland i Ullared? Galaxen bjuder på en unik rymdupplevelse med klätternät, rutschkanor och interaktiva lek-stationer – perfekt att kombinera med en shoppingtur till Gekås. Kalaspaketen inkluderar eget kalasrum, mat och dryck. Barnen utforskar galaxer medan föräldrarna njuter av en stressfri fest. Använd Svara Nu för att skicka inbjudan digitalt, samla OSA och låta gästerna rösta på datum – allt helt gratis!",
      en: "Planning a kids party at Galaxen Adventure Land in Ullared? Galaxen offers a unique space-themed experience with climbing nets, slides, and interactive play stations – perfect to combine with a shopping trip to Gekås. Party packages include a private party room, food, and drinks. Kids explore galaxies while parents enjoy a stress-free celebration. Use Svara Nu to send invitations digitally, collect RSVPs, and let guests vote on dates – all completely free!",
    },
    website: "https://www.galaxenullared.se",
    menus: [
      {
        id: "galaxen-stjarn",
        name: { sv: "Stjärnkalaset", en: "Star Party" },
        pricePerChild: 229,
        includes: {
          sv: "Entré, kalasrum 60 min, festdukning, saft, popcorn och godispåse",
          en: "Entry, party room 60 min, party setup, juice, popcorn, and candy bag",
        },
      },
      {
        id: "galaxen-galax",
        name: { sv: "Galaxkalaset", en: "Galaxy Party" },
        pricePerChild: 289,
        includes: {
          sv: "Entré, kalasrum 60 min, pizza, saft, glass, godispåse och present till kalasbarnet",
          en: "Entry, party room 60 min, pizza, juice, ice cream, candy bag, and gift for birthday child",
        },
      },
    ],
    partyRooms: [
      { id: "galaxen-mars", name: "Mars-rummet" },
      { id: "galaxen-jupiter", name: "Jupiter-rummet" },
    ],
  },
];
